import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { formatApiErrorDetail } from "@/lib/api";

const TXT = {
  fr: {
    tag: "[ CONNEXION ]", title: "Connecte-toi pour télécharger ta vidéo.", sub: "Ton projet est gardé : le téléchargement reprend juste après.",
    google: "Continuer avec Google", or: "ou par email", name: "Ton nom ou pseudo", email: "ton@email.com", pwd: "Mot de passe", pwdNew: "Mot de passe (6 caractères min.)",
    cgv1: "J'ai lu et j'accepte les ", cgv2: "Conditions générales de vente", login: "Se connecter", register: "Créer mon compte",
    toRegister: "Pas encore de compte ?", toLogin: "Déjà un compte ?", swRegister: "Créer un compte", swLogin: "Se connecter", cgvErr: "Tu dois accepter les Conditions générales de vente pour créer un compte.",
  },
  en: {
    tag: "[ SIGN IN ]", title: "Sign in to download your video.", sub: "Your project is kept: the download resumes right after.",
    google: "Continue with Google", or: "or with email", name: "Your name or alias", email: "you@email.com", pwd: "Password", pwdNew: "Password (6 characters min.)",
    cgv1: "I have read and accept the ", cgv2: "Terms of sale", login: "Sign in", register: "Create my account",
    toRegister: "No account yet?", toLogin: "Already have an account?", swRegister: "Create an account", swLogin: "Sign in", cgvErr: "You must accept the Terms of sale to create an account.",
  },
};

const inputCls = "w-full bg-background border border-border px-4 py-3 text-sm focus:border-[#fc1c46] focus:outline-none transition-colors";

export default function AuthGateDialog({ open, onClose, onLoggedIn, afterLoginPath }) {
  const { login, register, loginWithGoogle } = useAuth();
  const { lang } = useI18n();
  const t = TXT[lang] || TXT.fr;
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cgvOk, setCgvOk] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const busy = useRef(false);
  if (!open) return null;

  const google = () => {
    try { if (afterLoginPath) localStorage.setItem("bc_after_login", afterLoginPath); } catch {}
    loginWithGoogle();
  };
  const submit = async (e) => {
    e.preventDefault();
    if (busy.current) return;
    setError("");
    if (isRegister && !cgvOk) { setError(t.cgvErr); return; }
    busy.current = true; setLoading(true);
    try {
      const data = isRegister ? await register(name, email, password, null, cgvOk, true) : await login(email, password);
      onLoggedIn?.(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      busy.current = false; setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9997] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center p-3 sm:p-6 overflow-y-auto" role="dialog" aria-modal="true" data-testid="auth-gate">
      <div className="relative w-full max-w-md bg-card border border-border p-7 sm:p-9 my-auto">
        <button onClick={onClose} aria-label="Fermer" data-testid="auth-gate-close" className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground"><X size={16} /></button>
        <p className="font-osd text-xs tracking-[0.25em] text-primary mb-3">{t.tag}</p>
        <h2 className="font-display text-xl sm:text-2xl font-extrabold tracking-tight mb-2" data-testid="auth-gate-title">{t.title}</h2>
        <p className="text-xs text-muted-foreground mb-6">{t.sub}</p>
        <button onClick={google} data-testid="auth-gate-google" className="w-full flex items-center justify-center gap-3 border border-border px-5 h-12 text-sm font-medium hover:border-foreground transition-colors">
          <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.3 6.1 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.3 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
          </svg>
          {t.google}
        </button>
        <div className="flex items-center gap-3 my-5 text-xs text-muted-foreground"><span className="flex-1 h-px bg-border" />{t.or}<span className="flex-1 h-px bg-border" /></div>
        <form onSubmit={submit} className="space-y-3">
          {isRegister && <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder={t.name} data-testid="auth-gate-name" className={inputCls} />}
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.email} data-testid="auth-gate-email" className={inputCls} />
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder={isRegister ? t.pwdNew : t.pwd} data-testid="auth-gate-password" className={inputCls} />
          {isRegister && (
            <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer select-none">
              <input type="checkbox" checked={cgvOk} onChange={(e) => setCgvOk(e.target.checked)} data-testid="auth-gate-cgv" className="mt-0.5 accent-[#FF453A]" />
              <span>{t.cgv1}<Link to="/cgv" target="_blank" rel="noopener" className="text-foreground underline underline-offset-4">{t.cgv2}</Link>.</span>
            </label>
          )}
          {error && <p className="text-sm text-primary" data-testid="auth-gate-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="auth-gate-submit" className="w-full bg-primary text-white font-bold px-5 h-12 hover:opacity-90 transition-opacity disabled:opacity-50">
            {loading ? "…" : isRegister ? t.register : t.login}
          </button>
        </form>
        <p className="mt-5 text-sm text-muted-foreground text-center">
          {isRegister ? t.toLogin : t.toRegister}{" "}
          <button type="button" onClick={() => { setIsRegister(!isRegister); setError(""); }} data-testid="auth-gate-switch" className="text-foreground underline underline-offset-4">
            {isRegister ? t.swLogin : t.swRegister}
          </button>
        </p>
      </div>
    </div>
  );
}
